import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import * as BsIcons from 'react-icons/bs';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiBookOpen, FiActivity, FiShield } = FiIcons;
const { BsLightningCharge, BsBrain } = BsIcons;

const Home = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const featuredResources = [
    {
      title: "Understanding BDNF",
      desc: "The 'Master Brain Fertilizer' that keeps your neurons young and resilient.",
      img: "https://cdn1.sharemyimage.com/smi/2026/01/01/3d-medical-background-with-male-head-brain-dna-strands.jpg"
    },
    {
      title: "Gamma Wave Secrets",
      desc: "How specific frequencies can unlock peak cognitive performance.",
      img: "https://cdn1.sharemyimage.com/smi/2026/01/01/scientist-interacts-with-digital-brain-model-hightech-research-lab-setting.jpg"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fadeInUp}>
            <span className="inline-block px-4 py-1.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
              Editorial Resource Hub
            </span>
            <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-tight mb-6">
              The Future of <span className="text-indigo-600">Brain Optimization</span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed">
              We decode the latest neuroscience research into actionable insights. Explore our guides on neuroplasticity, sound therapy, and cognitive longevity.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/resources" className="bg-slate-900 text-white px-8 py-4 rounded-xl font-bold flex items-center space-x-2">
                <span>Browse Guides</span>
                <SafeIcon icon={FiBookOpen} />
              </Link>
              <Link to="/review/the-brain-song" className="bg-indigo-600 text-white px-8 py-4 rounded-xl font-bold flex items-center space-x-2 shadow-lg shadow-indigo-100">
                <span>Read Top Review</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </motion.div>
          
          <motion.div className="relative" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}>
             <img src="https://cdn1.sharemyimage.com/smi/2026/01/01/man-with-brain-highlighted.jpg" alt="Brain Health" className="rounded-3xl shadow-2xl relative z-10" />
             <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-indigo-100 rounded-full blur-3xl opacity-50 -z-10"></div>
          </motion.div>
        </div>
      </section>

      {/* Recommended Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">Featured Editorial Reviews</h2>
          </div>
          <motion.div className="max-w-5xl mx-auto bg-white rounded-3xl overflow-hidden shadow-xl flex flex-col md:flex-row border border-slate-100" {...fadeInUp}>
            <div className="md:w-1/2 overflow-hidden">
              <img src="https://cdn1.sharemyimage.com/smi/2026/01/01/3d-medical-technology-design-male-figure-with-brain-highlighted.jpg" alt="Review Product" className="h-full w-full object-cover hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="md:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
              <div className="flex items-center space-x-2 text-amber-500 mb-4">
                <SafeIcon icon={BsLightningCharge} />
                <span className="font-bold text-xs uppercase tracking-widest">Editor's Choice 2025</span>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4">The Brain Song: A Scientific Review</h3>
              <p className="text-slate-600 mb-8">Can a 12-minute audio sequence truly activate BDNF and improve focus? We took a deep dive into the research behind this popular digital tool.</p>
              <Link to="/review/the-brain-song" className="text-indigo-600 font-bold flex items-center space-x-2 group">
                <span>Read Full Review</span>
                <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;